import React from 'react';

// Helper to extract field value from Contentful entry
// Handles both single-locale (direct) and multi-locale formats
export function getFieldValue(entry, fieldName, locale = 'en-US') {
  if (!entry || !entry.fields || !entry.fields[fieldName]) {
    return null;
  }
  
  const field = entry.fields[fieldName];

  // If this is a linked entry/asset (has sys and fields), return it directly.
  // These are not locale maps and shouldn't be treated as such.
  if (field && typeof field === 'object' && !Array.isArray(field) && field.sys && field.fields) {
    return field;
  }
  
  // If field is an object with locale keys (multi-locale)
  if (typeof field === 'object' && field !== null && !Array.isArray(field) && field[locale]) {
    return field[locale];
  }
  
  // If field is directly accessible (single-locale or already resolved)
  if (typeof field !== 'object' || Array.isArray(field)) {
    return field;
  }
  
  // Try to get the first available locale value
  const locales = Object.keys(field);
  if (locales.length > 0) {
    return field[locales[0]];
  }
  
  return field;
}

// Helper to extract rich text content
export function getRichText(entry, fieldName, locale = 'en-US') {
  const value = getFieldValue(entry, fieldName, locale);
  if (!value) return null;
  
  // If it's already a rich text object, return it
  if (value.nodeType === 'document' || (typeof value === 'object' && value.content)) {
    return value;
  }
  
  // For now, return the raw rich text object
  // You might want to use @contentful/rich-text-react-renderer later
  return value;
}

/**
 * Extracts rich text from Contentful entry fields with robust locale handling
 * Similar to how Committee component extracts bio
 * @param {Object} entry - Contentful entry object
 * @param {string} fieldName - Name of the field to extract
 * @returns {Object|null} Rich text object or null if not found
 */
export function extractRichTextFromFields(entry, fieldName) {
  if (!entry || !entry.fields || !entry.fields[fieldName]) {
    return null;
  }
  
  const field = entry.fields[fieldName];
  
  // Check if it's already a document (rich text object)
  if (field.nodeType === 'document' || (field.content && Array.isArray(field.content))) {
    return field;
  }
  
  // Handle multi-locale format
  if (typeof field === 'object' && field !== null && !Array.isArray(field)) {
    // Try 'en-US' first
    if (field['en-US']) {
      return field['en-US'];
    } else {
      // Get first key that has nodeType or content (not metadata properties)
      const localeKeys = Object.keys(field).filter(key => {
        if (key === 'tags' || key === 'concepts' || key === 'metadata') return false;
        const value = field[key];
        return value && (value.nodeType === 'document' || (value.content && Array.isArray(value.content)));
      });
      if (localeKeys.length > 0) {
        return field[localeKeys[0]];
      } else {
        // If no locale keys found, maybe the object itself is the rich text
        if (field.nodeType === 'document' || (field.content && Array.isArray(field.content))) {
          return field;
        }
      }
    }
  }
  
  return field;
}

/**
 * Renders Contentful rich text to React elements
 * @param {Object} richText - Rich text object from Contentful
 * @returns {Array|null} Array of React paragraph elements or null
 */
export function renderRichText(richText) {
  if (!richText) {
    return null;
  }
  
  // Handle both document nodeType and direct content array
  let content = null;
  if (richText.nodeType === 'document') {
    content = richText.content;
  } else if (richText.content) {
    content = richText.content;
  } else {
    return null;
  }
  
  if (!content || !Array.isArray(content) || content.length === 0) {
    return null;
  }
  
  const paragraphs = content
    .filter(node => node && node.nodeType === 'paragraph')
    .map((node, index) => {
      if (!node.content || !Array.isArray(node.content)) {
        return null;
      }

      const children = node.content
        .filter(textNode => textNode && textNode.nodeType === 'text')
        .map((textNode, i) => {
          const text = textNode.value || '';
          if (!text) return null;

          let child = text;

          if (Array.isArray(textNode.marks) && textNode.marks.length > 0) {
            textNode.marks.forEach(mark => {
              if (!mark || !mark.type) return;
              if (mark.type === 'bold') {
                child = <strong key={`b-${i}`}>{child}</strong>;
              } else if (mark.type === 'italic') {
                child = <em key={`i-${i}`}>{child}</em>;
              }
            });
          }

          return <React.Fragment key={i}>{child}</React.Fragment>;
        })
        .filter(Boolean);

      if (!children || children.length === 0) {
        return null;
      }

      return <p key={index}>{children}</p>;
    })
    .filter(Boolean);

  return paragraphs.length > 0 ? paragraphs : null;
}

// Helper to get asset URL
export function getAssetUrl(asset, locale = 'en-US') {
  if (!asset) {
    return null;
  }
  
  // Handle case where asset is already a URL string
  if (typeof asset === 'string') {
    return asset;
  }
  
  // Check if asset has fields
  if (!asset.fields) {
    return null;
  }

  let fileField = asset.fields.file;

  if (!fileField) {
    return null;
  }

  // Some Contentful responses wrap the actual file under additional nesting,
  // e.g. asset.fields.file.fields.file or similar. Unwrap a couple of common cases.
  if (fileField.fields) {
    if (fileField.fields.file) {
      fileField = fileField.fields.file;
    } else if (fileField.fields[locale]) {
      fileField = fileField.fields[locale];
    }
  }
  
  // Handle multi-locale format where fileField is an object keyed by locale
  if (typeof fileField === 'object' && !Array.isArray(fileField) && fileField[locale] && fileField[locale].url) {
    let url = fileField[locale].url;
    if (url && url.startsWith('//')) {
      url = `https:${url}`;
    }
    return url;
  }
  
  // Handle single-locale or direct format: { url, fileName, contentType, ... }
  if (fileField.url) {
    let url = fileField.url;
    if (url && url.startsWith('//')) {
      url = `https:${url}`;
    }
    return url;
  }
  
  // Try first available locale if it's a locale map
  if (typeof fileField === 'object' && !Array.isArray(fileField)) {
    const locales = Object.keys(fileField);
    if (locales.length > 0 && fileField[locales[0]]?.url) {
      let url = fileField[locales[0]].url;
      if (url && url.startsWith('//')) {
        url = `https:${url}`;
      }
      return url;
    }
  }
  
  // Fallback: search deeply for a url property
  const visited = new Set();
  function findUrlDeep(obj) {
    if (!obj || typeof obj !== 'object') return null;
    if (visited.has(obj)) return null;
    visited.add(obj);

    if (typeof obj.url === 'string') {
      let url = obj.url;
      if (url && url.startsWith('//')) {
        url = `https:${url}`;
      }
      return url;
    }

    for (const key of Object.keys(obj)) {
      const child = obj[key];
      if (typeof child === 'object') {
        const found = findUrlDeep(child);
        if (found) return found;
      }
    }
    return null;
  }

  return findUrlDeep(fileField);
}

// Helper to map navigation items
export function mapNavigationItem(item) {
  if (!item) return null;
  
  const label = getFieldValue(item, 'label');
  
  // For linked entries (like targetPage), we need to access them directly from fields
  // getFieldValue is for simple field values, not linked entries
  let targetPage = null;
  if (item.fields && item.fields.targetPage) {
    const targetPageField = item.fields.targetPage;
    // Handle locale variations - check if it's wrapped in locale keys
    if (targetPageField['en-US'] && targetPageField['en-US'].sys) {
      targetPage = targetPageField['en-US'];
    } else if (targetPageField.sys) {
      // Already resolved entry
      targetPage = targetPageField;
    } else {
      // Try first available locale
      const locales = Object.keys(targetPageField);
      for (const locale of locales) {
        if (targetPageField[locale] && targetPageField[locale].sys) {
          targetPage = targetPageField[locale];
          break;
        }
      }
    }
  }
  
  const externalUrl = getFieldValue(item, 'externalUrl');
  const children = getFieldValue(item, 'children') || [];
  
  // Check if targetPage is a resolved entry (has sys and fields)
  let mappedTargetPage = null;
  if (targetPage && targetPage.sys && targetPage.fields) {
    // Extract slug from the targetPage entry
    const slug = getFieldValue(targetPage, 'slug');
    
    if (slug) {
      // Remove leading slash if present (we'll add it in the Link component)
      const cleanSlug = slug.startsWith('/') ? slug.slice(1) : slug;
      mappedTargetPage = {
        id: targetPage.sys.id,
        slug: cleanSlug,
      };
    } else {
      // Debug: log when targetPage exists but slug is missing
      console.warn('mapNavigationItem: targetPage exists but slug is missing', {
        targetPage,
        hasSys: !!targetPage.sys,
        hasFields: !!targetPage.fields,
        fieldsKeys: targetPage.fields ? Object.keys(targetPage.fields) : [],
        slugField: targetPage.fields?.slug,
      });
    }
  } else if (item.fields && item.fields.targetPage) {
    // Debug: log when targetPage field exists but wasn't properly extracted
    console.warn('mapNavigationItem: targetPage field exists but entry structure is unexpected', {
      rawTargetPage: item.fields.targetPage,
      extractedTargetPage: targetPage,
    });
  }
  
  const mapped = {
    label,
    targetPage: mappedTargetPage,
    externalUrl,
    children: children.filter(Boolean).map(mapNavigationItem).filter(Boolean),
  };
  
  // Only return null if we have no label (invalid item)
  // Don't filter out items just because targetPage mapping failed
  if (!label) {
    return null;
  }
  
  // Debug: log the mapped item to see what we're getting
  if (targetPage && !mappedTargetPage) {
    console.warn('mapNavigationItem: targetPage was lost in mapping', {
      originalTargetPage: targetPage,
      mapped,
      label,
    });
  }
  
  return mapped;
}

// Helper to map page sections
export function mapPageSections(page) {
  if (!page || !page.fields) {
    console.warn('Page has no fields:', page);
    return [];
  }
  
  const sections = getFieldValue(page, 'sections') || [];
  
  if (!Array.isArray(sections)) {
    console.error('Sections is not an array:', sections);
    return [];
  }
  
  if (sections.length === 0) {
    console.warn('No sections found on page');
    return [];
  }
  
  console.log(sections)

  console.log('Sections:', sections)
  const mapped = sections.map((section, index) => {
    // Check if section is a link that wasn't resolved
    if (section.sys && section.sys.type === 'Link' && !section.fields) {
      console.warn(`Section ${index} is an unresolved link:`, section);
      return null;
    }
    
    const componentType = getFieldValue(section, 'componentType');
    
    if (!componentType) {
      console.warn(`Section ${index} has no componentType`);
    }
    
    return {
      id: section.sys?.id || `section-${index}`,
      componentType,
      data: section,
    };
  }).filter(Boolean); // Remove null entries
  
  return mapped;
}

